/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FormFieldProps } from "@octopusdeploy/design-system-octopus-components";
import type { TagResource, TagSetResource, TagSetScope } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { repository } from "~/clientInstance";
import BaseComponent from "~/components/BaseComponent/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { MultiSelect } from "~/components/MultiSelect/MultiSelect";
import type { SelectItem } from "~/components/VirtualListWithKeyboard/SelectItem";
import * as tagsets from "~/components/tagSets";
import { isFeatureToggleEnabled } from "../FeatureToggle/New/FeatureToggleContext";
import FeatureToggleVisibility from "../FeatureToggle/New/FeatureToggleVisibility";
import Tag from "../Tag";
import TagSetSelect from "../form/TagSet/TagSetSelect";
interface TagSetMultiSelectionProps extends FormFieldProps<string[]> {
    doBusyTask: DoBusyTask;
    items?: TagSetResource[] | (() => TagSetResource[]);
    autoFocus?: boolean;
    showFreeTextTagSet?: boolean;
    scopes: TagSetScope[];
    disableCloseOnSelected?: boolean;
}
interface TagSetSelectionState {
    tagSets: TagSetResource[];
}
const TagResourceMultiSelect = MultiSelect<TagResource>();
export class TagSetMultiSelect extends BaseComponent<TagSetMultiSelectionProps, TagSetSelectionState> {
    constructor(props: TagSetMultiSelectionProps) {
        super(props);
        let sortedTagSets = this.props.items ? (typeof this.props.items === "function" ? this.props.items() : this.props.items).sort((t1, t2) => t1.SortOrder - t2.SortOrder) : null!;
        sortedTagSets = this.props.showFreeTextTagSet ? sortedTagSets : sortedTagSets?.filter((ts) => ts.Type !== "FreeText");
        this.state = {
            tagSets: sortedTagSets,
        };
    }
    async componentDidMount() {
        await this.props.doBusyTask(async () => {
            if (!this.state.tagSets) {
                const scopes: TagSetScope[] = isFeatureToggleEnabled("extended-tag-sets") ? this.props.scopes : ["Tenant"];
                let tagSets = await repository.TagSets.getAllByScopes(scopes);
                tagSets = this.props.showFreeTextTagSet ? tagSets : tagSets?.filter((ts) => ts.Type !== "FreeText");
                this.setState({ tagSets: tagSets.sort((t1, t2) => t1.SortOrder - t2.SortOrder) });
            }
        });
    }
    chipRenderer = (tagSet: TagSetResource, r: TagResource | SelectItem, onRequestDelete: () => void) => {
        const tag = tagSet.Tags.find((t) => t.Id === r.Id);
        return <Tag onRequestDelete={onRequestDelete} deleteButtonAccessibleName={`Delete ${tag!.Name}`} description={tag!.Description} tagName={tag!.Name} tagColor={tag!.Color}/>;
    };
    onChange(tagSet: TagSetResource, tagIds: string[]) {
        const oldState = tagsets.groupByTagSet(this.props.value!);
        const tagSetFromState = oldState.find((x) => x.name === tagSet.Name);
        const squashedList = tagIds.map((id) => {
            const tag = tagSet.Tags.find((p) => p.Id === id);
            return tag ? tag.CanonicalTagName : id;
        });
        if (tagSetFromState) {
            tagSetFromState.tags = squashedList;
        }
        else {
            oldState.push({ name: tagSet.Name, tags: squashedList });
        }
        const newState = tagsets.flattenGroup(oldState);
        this.props.onChange!(newState);
    }
    render() {
        if (!this.state.tagSets) {
            return null;
        }
        const options = this.state.tagSets.map((ts, index) => {
            if (ts.Type !== "FreeText" && (!ts.Tags || ts.Tags.length === 0)) {
                return null;
            }
            const selected = this.props.value ? ts.Tags.filter((t) => this.props.value!.indexOf(t.CanonicalTagName) !== -1).map((t) => t.Id) : [];
            const accessibleName = `tagSetFilter-${ts.Name}`;
            return (<FeatureToggleVisibility toggle="extended-tag-sets" key={ts.Id} disabledContent={<TagResourceMultiSelect accessibleName={accessibleName} onChange={(items) => this.onChange(ts, items)} key={ts.Id} value={selected} items={ts.Tags ? ts.Tags.sort((t1, t2) => t1.SortOrder - t2.SortOrder) : null!} renderChip={(r: TagResource | SelectItem, dc) => this.chipRenderer(ts, r, dc)} fieldName={`${ts.Name} tags`} description={ts.Description} autoFocus={this.props.autoFocus && index === 0} sortItems={false} // Maintain sort order of items passed through
                 disableCloseOnSelected={this.props.disableCloseOnSelected}/>}>
                    <TagSetSelect TagSet={ts} value={this.props.value ? this.props.value.filter((v) => v.startsWith(ts.Name + "/")) : []} onChange={(newValues) => {
                    this.onChange(ts, newValues);
                }} doBusyTask={this.props.doBusyTask} autoFocus={this.props.autoFocus && index === 0}/>
                </FeatureToggleVisibility>);
        });
        return <div>{options}</div>;
    }
    static displayName = "TagSetMultiSelect";
}
